import type { OrderItem, PosVariant, FulfillmentMode } from '../types';

export interface PosTotals {
  totalWeight: number;
  totalGoods: number;
  totalDiscount: number;
  totalVat: number;
  /** Tổng phải trả gồm VAT (= totalGoods - totalDiscount + totalVat). */
  grandTotal: number;
}

/** Single source of truth tính tổng đơn POS (PaymentFooter + checkout cùng dùng). */
export function computePosTotals(items: OrderItem[]): PosTotals {
  let totalWeight = 0;
  let totalGoods = 0;
  let totalDiscount = 0;
  let totalVat = 0;
  let totalPayment = 0;

  for (const item of items) {
    const w = item.weight ?? 0;
    totalWeight += item.trackingMode === 'serial' ? w : w * item.quantity;
    totalGoods += item.price * item.quantity;
    totalDiscount += item.discountAmount;
    totalPayment += item.totalAmount;
    totalVat += Math.round((item.totalAmount * (item.vat ?? 0)) / 100);
  }

  return {
    totalWeight,
    totalGoods,
    totalDiscount,
    totalVat,
    grandTotal: totalPayment + totalVat,
  };
}

/** Recalc discountAmount + totalAmount theo price/discountPercent/quantity hiện tại. */
export function recalcItem(item: OrderItem, fulfillmentMode: FulfillmentMode): OrderItem {
  const isQuantityMode = item.trackingMode === 'quantity' || fulfillmentMode === 'PREORDER';
  const discPerUnit = (item.price * item.discountPercent) / 100;
  const discountAmount = isQuantityMode
    ? Math.round(discPerUnit * item.quantity)
    : Math.round(discPerUnit);
  const totalAmount = isQuantityMode
    ? item.price * item.quantity - discountAmount
    : item.price - discountAmount;
  return { ...item, discountAmount, totalAmount };
}

/**
 * Ghép thông tin serial mới (đã map từ lookupVariants) vào 1 dòng đơn đang có:
 * - cập nhật serial/giá/KL/phí công/kho + snapshot gold-price
 * - GIỮ discountPercent (CK%) + sellerStaffId (NV bán) + id dòng
 * - reset warehouseStaffId nếu không còn hợp lệ trong danh sách kho mới
 */
export function applySwappedVariant(
  item: OrderItem,
  variant: PosVariant,
  fulfillmentMode: FulfillmentMode,
): OrderItem {
  const warehouseStaffId =
    item.warehouseStaffId && variant.warehouse_staff_ids?.includes(item.warehouseStaffId)
      ? item.warehouseStaffId
      : undefined;

  const merged: OrderItem = {
    ...item,
    specificationCode: variant.specification_code,
    pricingMethod: variant.pricing_method ?? item.pricingMethod,
    price: variant.price,
    weight: variant.weight,
    laborFee: variant.labor_fee,
    vat: variant.vat,
    warehouseStaffIds: variant.warehouse_staff_ids,
    warehouseStaffId,
    instrumentCode: variant.instrument_code,
    appliedBid: variant.applied_bid,
    appliedAsk: variant.applied_ask,
    appliedTs: variant.applied_ts,
    appliedOverride: variant.override_applied,
  };
  return recalcItem(merged, fulfillmentMode);
}
